import React, { ReactNode } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';
import smaroLogo from './smaro-logo';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="relative h-[52px] items-center">
          <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center">
            <div className="ml-4 w-10 text-xl font-bold text-white">
              <SmaroLogo />
            </div>
          </div>
          <div className="absolute top-1/2 left-[250px] h-8 -translate-y-1/2">{Secondary}</div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            <div className="flex items-center justify-center space-x-2">{children}</div>
          </div>
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 select-none items-center">
            {UndoRedo}
            <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>
            {PatientInfo}
            <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>
            <div className="flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-primary-dark mt-2 h-full w-full text-white"
                  >
                    <Icons.GearSettings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon
                      ? Icons[option.icon as keyof typeof Icons]
                      : null;
                    return (
                      <DropdownMenuItem
                        key={index}
                        onSelect={option.onClick}
                        className="flex items-center gap-2 py-2"
                      >
                        {IconComponent && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={IconComponent.name} />
                          </span>
                        )}
                        <span className="flex-1">{option.title}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;

const SmaroLogo = () => {
  return (
    <svg
      width="153"
      height="41"
      viewBox="0 0 183 71"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M29.7028 26.9905C28.8463 25.6441 27.5722 24.5923 25.8802 23.8139C24.1883 23.0355 21.9115 22.4044 19.0916 21.8995C16.5851 21.4367 14.7469 20.8477 13.5772 20.1114C12.4075 19.3751 11.8226 18.3022 11.8226 16.8717C11.8226 15.6094 12.3866 14.5997 13.5354 13.8213C14.6634 13.0429 16.2718 12.6432 18.3606 12.6432C19.5721 12.6432 20.9507 12.9377 22.4964 13.5057C24.0212 14.0948 25.2118 14.7259 26.0891 15.4201C26.486 15.7146 26.9246 15.8619 27.426 15.8619C28.1779 15.8619 28.8463 15.5674 29.4103 14.9994C29.9743 14.4103 30.2458 13.7371 30.2458 12.9798C30.2458 11.9911 29.7654 11.1075 28.8046 10.3502C27.5931 9.31936 26.0682 8.49892 24.2301 7.86781C22.3919 7.2367 20.4494 6.92114 18.3814 6.92114C16.0838 6.92114 13.9741 7.32085 12.115 8.14129C10.2351 8.96173 8.75206 10.1398 7.66589 11.6755C6.57971 13.2112 6.03662 15.0414 6.03662 17.1241C6.03662 22.8251 9.65025 26.2121 16.8566 27.285C19.7183 27.7268 21.8071 28.3579 23.123 29.1994C24.439 30.0408 25.1074 31.2189 25.1074 32.7125C25.1074 33.9327 24.439 34.9635 23.1021 35.805C21.7653 36.6254 20.1778 37.0462 18.3397 37.0462C16.6269 37.0462 15.1438 36.7937 13.8905 36.3099C12.6372 35.805 11.3631 35.0056 10.0262 33.9117C9.52492 33.512 8.98183 33.3226 8.35519 33.3226C7.56145 33.3226 6.89303 33.6171 6.32905 34.1851C5.76507 34.7742 5.49353 35.4474 5.49353 36.2047C5.49353 37.1724 5.93218 38.056 6.83036 38.8343C8.43874 40.2648 10.1724 41.2746 12.0106 41.8637C13.8487 42.4527 15.9584 42.7682 18.3397 42.7682C20.5747 42.7682 22.6635 42.3475 24.6061 41.506C26.5487 40.6645 28.1153 39.4444 29.285 37.8666C30.4547 36.2888 31.0396 34.4586 31.0396 32.376C30.9978 30.125 30.5592 28.3368 29.7028 26.9905Z"
        fill="#FFFFFF"
      />
      <path
        d="M73.4422 18.6389C72.1889 16.6404 69.933 15.6306 66.6745 15.6306C64.7319 15.6306 62.9982 16.1986 61.4525 17.3136C59.9277 18.4496 58.7788 19.7118 58.0269 21.1002C56.878 17.4608 54.2252 15.6306 50.0685 15.6306C48.3975 15.6306 46.8726 16.0514 45.494 16.8928C44.1363 17.7343 43.0084 18.7651 42.1102 19.9432V19.1438C42.1102 18.2813 41.8386 17.566 41.2747 16.977C40.7107 16.3879 40.0005 16.1145 39.1441 16.1145C38.2877 16.1145 37.5775 16.409 37.0135 16.977C36.4495 17.566 36.178 18.2813 36.178 19.1438V39.697C36.178 40.5595 36.4495 41.2748 37.0135 41.8638C37.5775 42.4528 38.2877 42.7263 39.1441 42.7263C40.0005 42.7263 40.7107 42.4318 41.2747 41.8638C41.8386 41.2748 42.1102 40.5595 42.1102 39.697V26.9485C42.1102 25.2656 42.6324 23.8561 43.6768 22.7411C44.7212 21.6262 46.1625 21.0792 48.0006 21.0792C49.7761 21.0792 51.0294 21.6051 51.7396 22.657C52.4498 23.7088 52.8048 25.1394 52.8048 26.9696V39.718C52.8048 40.5805 53.0764 41.2958 53.6404 41.8848C54.2043 42.4739 54.9145 42.7474 55.7709 42.7474C56.6274 42.7474 57.3376 42.4528 57.9015 41.8848C58.4655 41.2958 58.737 40.5805 58.737 39.718V26.9696C58.737 25.2866 59.2593 23.8771 60.3037 22.7622C61.3481 21.6472 62.7893 21.1002 64.6275 21.1002C66.403 21.1002 67.6562 21.6262 68.3664 22.678C69.0766 23.7299 69.4317 25.1604 69.4317 26.9906V39.7391C69.4317 40.6016 69.7033 41.3168 70.2672 41.9059C70.8312 42.4949 71.5414 42.7684 72.3978 42.7684C73.2542 42.7684 73.9644 42.4739 74.5284 41.9059C75.0924 41.3168 75.3639 40.6016 75.3639 39.7391V26.9906C75.3221 23.4143 74.6955 20.6374 73.4422 18.6389Z"
        fill="#FFFFFF"
      />
      <path
        d="M105.505 15.9672C104.941 15.3992 104.231 15.1257 103.375 15.1257C102.518 15.1257 101.808 15.4203 101.244 15.9883C100.68 16.5773 100.409 17.2925 100.409 18.1551V18.1971C99.4479 17.2925 98.2782 16.5563 96.8996 15.9883C95.5209 15.3992 94.0379 15.1257 92.4504 15.1257C90.2363 15.1257 88.231 15.7148 86.3929 16.8928C84.5547 18.0709 83.1135 19.7118 82.0691 21.8155C81.0247 23.9192 80.4816 26.2964 80.4816 28.9471C80.4816 31.5977 81.0247 33.9749 82.0899 36.0576C83.1552 38.1402 84.6174 39.7811 86.4555 40.9592C88.2937 42.1373 90.3616 42.7263 92.6384 42.7263C94.1841 42.7263 95.6672 42.3897 97.0667 41.7165C98.4662 41.0434 99.5732 40.2229 100.409 39.2552C100.451 40.1177 100.722 40.833 101.265 41.401C101.808 41.969 102.518 42.2425 103.375 42.2425C104.231 42.2425 104.941 41.948 105.505 41.38C106.069 40.7909 106.341 40.0757 106.341 39.2131V18.1551C106.341 17.2715 106.069 16.5352 105.505 15.9672ZM98.6333 34.9005C97.2755 36.4783 95.5418 37.2567 93.3904 37.2567C91.2389 37.2567 89.5052 36.4783 88.1684 34.9005C86.8315 33.3228 86.1631 31.3453 86.1631 28.9471C86.1631 26.5488 86.8315 24.5714 88.1893 22.9725C89.547 21.3737 91.2598 20.5743 93.3695 20.5743C95.5001 20.5743 97.2547 21.3737 98.6124 22.9725C99.9701 24.5714 100.639 26.5488 100.639 28.9471C100.659 31.3453 99.991 33.3228 98.6333 34.9005Z"
        fill="#FFFFFF"
      />
      <path
        d="M139.678 38.8555C139.428 38.4347 139.093 38.0771 138.676 37.8036C138.216 37.5722 137.924 37.2356 137.777 36.7938C137.652 36.3521 137.506 35.6158 137.381 34.585C137.381 34.0801 137.339 33.5752 137.234 33.0493C137.046 30.5879 135.897 28.6315 133.83 27.1799C135.25 26.2543 136.399 24.9921 137.297 23.4143C138.195 21.8365 138.634 20.2167 138.634 18.5547C138.634 16.5352 138.174 14.705 137.276 13.0851C136.378 11.4653 135.166 10.182 133.642 9.25636C132.117 8.33073 130.508 7.86792 128.796 7.86792H114.571C113.714 7.86792 113.004 8.16244 112.419 8.73044C111.834 9.31948 111.563 10.0347 111.563 10.8973V39.6549C111.563 40.5174 111.918 41.2327 112.628 41.8217C113.338 42.4107 114.174 42.6842 115.114 42.6842C115.908 42.6842 116.534 42.4107 117.015 41.8427C117.495 41.2747 117.725 40.5384 117.725 39.6549V29.9989H126.957C128.399 29.9989 129.527 30.3565 130.299 31.0507C131.072 31.745 131.469 32.6916 131.469 33.8907C131.469 38.0771 132.66 40.833 135.02 42.2004C135.689 42.558 136.399 42.7473 137.193 42.7473C138.216 42.7473 138.968 42.4528 139.469 41.8427C139.866 41.3378 140.054 40.7909 140.054 40.2019C140.054 39.739 139.929 39.2973 139.678 38.8555ZM131.636 21.7734C131.281 22.7201 130.822 23.4774 130.258 24.0454C129.694 24.6134 129.13 24.8869 128.524 24.8869H117.704V13.59H128.524C129.109 13.59 129.694 13.8214 130.258 14.2842C130.822 14.747 131.281 15.3781 131.636 16.1565C131.991 16.9349 132.179 17.7974 132.179 18.723C132.179 19.7959 132.012 20.8267 131.636 21.7734Z"
        fill="#FFFFFF"
      />
      <path
        d="M173.621 56.842H90.3198C88.3564 56.842 86.748 58.4619 86.748 60.4394C86.748 62.4169 88.3564 64.0367 90.3198 64.0367H173.6C175.564 64.0367 177.172 62.4169 177.172 60.4394C177.172 58.4619 175.585 56.842 173.621 56.842Z"
        fill="#FFFFFF"
      />
      <path
        d="M15.0967 61.8421C15.8605 59.9851 14.9849 57.8561 13.141 57.0868C11.2971 56.3176 9.18321 57.1995 8.41943 59.0565C7.65566 60.9135 8.53125 63.0425 10.3751 63.8117C12.219 64.581 14.3329 63.6991 15.0967 61.8421Z"
        fill="#FFFFFF"
      />
      <path
        d="M27.3841 64.0789C29.3799 64.0789 30.9978 62.4494 30.9978 60.4395C30.9978 58.4295 29.3799 56.8 27.3841 56.8C25.3884 56.8 23.7705 58.4295 23.7705 60.4395C23.7705 62.4494 25.3884 64.0789 27.3841 64.0789Z"
        fill="#FFFFFF"
      />
      <path
        d="M42.7369 64.0789C44.7327 64.0789 46.3505 62.4494 46.3505 60.4395C46.3505 58.4295 44.7327 56.8 42.7369 56.8C40.7412 56.8 39.1233 58.4295 39.1233 60.4395C39.1233 62.4494 40.7412 64.0789 42.7369 64.0789Z"
        fill="#FFFFFF"
      />
      <path
        d="M58.8833 64.0789C60.879 64.0789 62.4969 62.4494 62.4969 60.4395C62.4969 58.4295 60.879 56.8 58.8833 56.8C56.8875 56.8 55.2697 58.4295 55.2697 60.4395C55.2697 62.4494 56.8875 64.0789 58.8833 64.0789Z"
        fill="#FFFFFF"
      />
      <path
        d="M74.6329 64.0789C76.6287 64.0789 78.2465 62.4494 78.2465 60.4395C78.2465 58.4295 76.6287 56.8 74.6329 56.8C72.6372 56.8 71.0193 58.4295 71.0193 60.4395C71.0193 62.4494 72.6372 64.0789 74.6329 64.0789Z"
        fill="#FFFFFF"
      />
      <path
        d="M174.812 15.7988C173.392 13.1271 171.449 11.0024 168.963 9.40356C169.13 9.9926 169.548 11.381 169.757 12.0542C169.778 12.1173 169.799 12.1805 169.82 12.2436C170.133 13.2113 170.843 15.3991 169.214 17.9446C170.196 17.5028 171.177 17.2925 172.138 17.3345C173.454 17.3976 174.457 17.9025 174.979 18.176C175.042 18.2181 175.104 18.2391 175.146 18.2602C175.376 18.3653 175.689 18.5126 176.003 18.6809C175.71 17.6922 175.313 16.7245 174.812 15.7988Z"
        fill="#FFFFFF"
      />
      <path
        d="M176.796 21.9627C175.585 22.7831 173.726 23.7719 172.055 23.7088C171.7 23.6877 171.344 23.6246 171.031 23.4984C170.843 23.4353 170.655 23.3511 170.488 23.267C170.53 23.7719 170.572 24.2978 170.572 24.8237C170.572 27.0537 170.133 29.0522 169.235 30.8614C168.337 32.6706 167.104 34.1011 165.538 35.174C163.95 36.2258 162.175 36.7728 160.211 36.7728C158.206 36.7728 156.41 36.2469 154.843 35.195C153.276 34.1432 152.065 32.7126 151.188 30.8824C150.31 29.0522 149.872 27.0326 149.872 24.8027C149.872 22.5728 150.31 20.5532 151.188 18.723C152.065 16.8928 153.276 15.4623 154.843 14.4104C156.41 13.3586 158.185 12.8326 160.211 12.8326C161.611 12.8326 162.906 13.1061 164.096 13.632C164.723 11.2549 167.209 8.98285 167.543 8.68834L167.647 8.60419C165.391 7.44715 162.906 6.87915 160.19 6.87915C156.994 6.87915 154.112 7.67856 151.563 9.27737C149.015 10.8762 147.01 13.043 145.59 15.7778C144.148 18.5126 143.438 21.5209 143.438 24.8027C143.438 28.0845 144.148 31.0928 145.59 33.8276C147.031 36.5624 149.015 38.7292 151.563 40.328C154.112 41.9269 156.994 42.7263 160.19 42.7263C163.386 42.7263 166.269 41.9269 168.817 40.328C171.365 38.7292 173.371 36.5624 174.812 33.8276C176.253 31.0928 176.984 28.0845 176.984 24.8027C176.984 23.856 176.922 22.8883 176.796 21.9627Z"
        fill="#FFFFFF"
      />
      <path
        d="M168.963 12.3068C168.692 11.4232 168.086 9.36157 168.086 9.36157C168.086 9.36157 164.305 12.7065 164.827 15.2099C165.162 17.0191 166.206 17.9447 166.206 17.9447C166.206 17.9447 163.95 15.8831 161.59 17.9447C160.775 18.4285 160.378 18.5968 160.378 18.5968C160.378 18.5968 162.279 19.3752 162.989 19.8801C163.699 20.406 165.914 20.8057 168.044 18.134C170.175 15.4623 169.235 13.1903 168.963 12.3068Z"
        fill="#FFFFFF"
      />
      <path
        d="M174.812 19.0176C173.976 18.6179 171.888 17.3136 168.942 19.0386C165.997 20.7636 166.06 23.0146 166.457 23.814C166.854 24.6134 167.313 26.6119 167.313 26.6119C167.313 26.6119 167.522 26.2333 168.128 25.497C170.509 23.4564 168.817 20.9109 168.817 20.9109C168.817 20.9109 169.569 22.089 171.303 22.699C173.705 23.5826 177.548 20.3429 177.548 20.3429C177.548 20.3429 175.627 19.4173 174.812 19.0176Z"
        fill="#FFFFFF"
      />
    </svg>
  );
};
